/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { graphql, useLazyLoadQuery } from 'react-relay';
import { css, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';
import { type RenderFn } from '@atlassian/forge-ui-types';
import { useInlineContext } from '../../../context/inline';
import { default as PlatformAvatar, AvatarItem as PlatformAvatarItem } from '@atlaskit/avatar';
import { default as PlatformBadge } from '@atlaskit/badge';
import { type user_forge_ui_userQuery as UserQuery } from './__generated__/user_forge_ui_userQuery.graphql';
import { RelaySuspensefulBoundary } from '../../../utils/RelaySuspensefulBoundary';

const containerStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		paddingLeft: 0,
		backgroundColor: 'transparent',
		cursor: 'default',
	},
});

export type UserProps = {
	accountId: string;
	hideDisplayName?: boolean;
};

const messages = defineMessages({
	unknownUser: {
		id: 'confluence.user.unknown.user.message',
		defaultMessage: 'Unknown User',
		description: 'This is a fallback message that displays when a username is not known.',
	},
});

const Avatar = ({
	name,
	picture,
	hideDisplayName,
}: {
	hideDisplayName?: boolean;
	name: string;
	picture: string;
}) => {
	if (hideDisplayName) {
		return <PlatformAvatar name={name} src={picture} />;
	}

	return (
		<div
			// Reduce Atlaskit padding to vertically-align with other components
			css={containerStyle}
		>
			<PlatformAvatarItem
				avatar={<PlatformAvatar name={name} src={picture} />}
				primaryText={name}
			/>
		</div>
	);
};

function UserInline({ name }: { name?: string }) {
	const intl = useIntl();
	return (
		<PlatformBadge appearance="primary">
			{name || intl.formatMessage(messages.unknownUser)}
		</PlatformBadge>
	);
}

const User = ({ accountId, hideDisplayName }: UserProps) => {
	const data = useLazyLoadQuery<UserQuery>(
		graphql`
			query user_forge_ui_userQuery($accountId: ID!) {
				user(accountId: $accountId) {
					name
					picture
				}
			}
		`,
		{
			accountId,
		},
	);

	const { picture, name } = data?.user ?? { picture: '', name: '' };
	const { inline } = useInlineContext();

	if (!picture && !name) {
		return null;
	}

	if (inline) {
		return <UserInline name={name} />;
	}
	return <Avatar name={name} picture={picture} hideDisplayName={hideDisplayName} />;
};

export const makeUser = (): RenderFn => {
	return function UserFnNext({ forgeDoc: { props } }) {
		const { accountId, hideDisplayName } = props as UserProps;
		return (
			<RelaySuspensefulBoundary>
				<User accountId={accountId} hideDisplayName={hideDisplayName} />
			</RelaySuspensefulBoundary>
		);
	};
};
